import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
  type Dispatch,
  type SetStateAction
} from 'react'
import { collection, query, where, onSnapshot } from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from './AuthContext'
import { useLocation } from './LocationContext'
import { formatDate } from '../utils/dateUtils'
import { toDate } from '../utils/firestoreUtils'
import type { Timestamp } from 'firebase/firestore'

/** Break period in a session */
interface SessionBreak {
  startTime: Timestamp | null
  endTime: Timestamp | null
  location?: {
    lat: number
    lng: number
  }
}

/** Check in/out data */
interface CheckData {
  time: string
  timestamp: Timestamp
  location?: {
    lat: number
    lng: number
    accuracy: number
  }
  photoUrl?: string
}

/** Session data structure */
export interface Session {
  id: string
  userId: string
  date: string
  status: 'active' | 'completed' | 'edited'
  checkIn: CheckData | null
  checkOut: CheckData | null
  breaks: SessionBreak[]
  totalWorkMinutes?: number
  totalBreakMinutes?: number
  notes?: string
  siteId?: string
  siteName?: string
  editedBy?: string
  editedAt?: Timestamp
}

/** Session context value */
interface SessionContextValue {
  currentSession: Session | null
  todaysSessions: Session[]
  loading: boolean
  error: string | null
  isClockedIn: boolean
  isOnBreak: boolean
  currentBreak: SessionBreak | null
  getElapsedTime: () => number
  getTotalBreakTime: () => number
  getWorkTime: () => number
  setCurrentSession: Dispatch<SetStateAction<Session | null>>
  setTodaysSessions: Dispatch<SetStateAction<Session[]>>
  clearError: () => void
}

/** Props for SessionProvider */
interface SessionProviderProps {
  children: ReactNode
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: SessionProviderProps): React.ReactElement {
  const { user, isApproved } = useAuth()
  const { startLocationTracking, stopLocationTracking, locationSettings } = useLocation()
  const [currentSession, setCurrentSession] = useState<Session | null>(null)
  const [todaysSessions, setTodaysSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Subscribe to today's active session for the current user
  useEffect(() => {
    if (!user || !isApproved) {
      setCurrentSession(null)
      setLoading(false)
      return
    }

    const today = formatDate(new Date())
    const sessionsRef = collection(db, 'sessions')

    // Get user's sessions for today
    // Note: We fetch all today's sessions to populate todaysSessions state,
    // then find the active one. A separate query just for active status would
    // require a composite index and duplicate fetching.
    const q = query(
      sessionsRef,
      where('userId', '==', user.uid),
      where('date', '==', today)
    )

    let retryCount = 0
    const maxRetries = 3
    const retryDelay = 2000 // 2 seconds

    const setupSubscription = (): (() => void) => {
      return onSnapshot(
        q,
        (snapshot) => {
          // Reset retry count on successful snapshot
          retryCount = 0

          // Store all sessions for today
          const allSessions = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Session[]
          setTodaysSessions(allSessions)

          // Find active session client-side
          const activeSessionDoc = snapshot.docs.find(doc => doc.data()['status'] === 'active')

          if (activeSessionDoc) {
            const sessionData = activeSessionDoc.data()
            setCurrentSession({
              id: activeSessionDoc.id,
              ...sessionData
            } as Session)
          } else {
            setCurrentSession(null)
          }
          setLoading(false)
          setError(null)
        },
        (err: unknown) => {
          if (import.meta.env.DEV) {
            console.error('SessionContext: Error fetching session:', err)
          }

          // Retry logic for failed subscriptions
          if (retryCount < maxRetries) {
            retryCount++
            setError(`Connection lost. Retrying (${retryCount}/${maxRetries})...`)
            setTimeout(() => {
              unsubscribe = setupSubscription()
            }, retryDelay * retryCount) // Exponential backoff
          } else {
            setError('Failed to load session data. Please refresh the page.')
            setLoading(false)
          }
        }
      )
    }

    let unsubscribe = setupSubscription()

    return () => unsubscribe()
  }, [user, isApproved])

  // Auto-start/stop location tracking based on session state
  useEffect(() => {
    if (currentSession && currentSession.checkOut === null && locationSettings.enabled) {
      // Active session - start tracking with session ID
      startLocationTracking(currentSession.id)
    } else {
      // No active session or tracking disabled - stop tracking
      stopLocationTracking()
    }
  }, [currentSession, locationSettings.enabled, startLocationTracking, stopLocationTracking])

  // Memoized derived values
  const isClockedIn = useMemo(() =>
    currentSession !== null && currentSession.checkOut === null,
    [currentSession]
  )

  const isOnBreak = useMemo(() =>
    currentSession?.breaks?.some((brk) => brk.startTime && !brk.endTime) ?? false,
    [currentSession]
  )

  const currentBreak = useMemo(() =>
    isOnBreak ? currentSession?.breaks?.find((brk) => brk.startTime && !brk.endTime) ?? null : null,
    [isOnBreak, currentSession]
  )

  // Calculate elapsed time since clock in
  const getElapsedTime = useCallback((): number => {
    if (!currentSession?.checkIn?.timestamp) return 0

    const checkInTime = toDate(currentSession.checkIn.timestamp)

    return Math.floor((Date.now() - checkInTime.getTime()) / 1000)
  }, [currentSession])

  // Calculate total break time
  const getTotalBreakTime = useCallback((): number => {
    if (!currentSession?.breaks?.length) return 0

    return currentSession.breaks.reduce((total, brk) => {
      if (!brk.startTime) return total

      const startTime = toDate(brk.startTime)
      const endTime = brk.endTime ? toDate(brk.endTime) : new Date()

      return total + Math.floor((endTime.getTime() - startTime.getTime()) / 1000)
    }, 0)
  }, [currentSession])

  // Get work time (elapsed - breaks)
  const getWorkTime = useCallback((): number => {
    return getElapsedTime() - getTotalBreakTime()
  }, [getElapsedTime, getTotalBreakTime])

  // Memoize clearError to prevent unnecessary re-renders
  const clearError = useCallback(() => setError(null), [])

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<SessionContextValue>(() => ({
    currentSession,
    todaysSessions,
    loading,
    error,
    isClockedIn,
    isOnBreak,
    currentBreak,
    getElapsedTime,
    getTotalBreakTime,
    getWorkTime,
    setCurrentSession,
    setTodaysSessions,
    clearError,
  }), [
    currentSession,
    todaysSessions,
    loading,
    error,
    isClockedIn,
    isOnBreak,
    currentBreak,
    getElapsedTime,
    getTotalBreakTime,
    getWorkTime,
    clearError,
  ])

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  )
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext)
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider')
  }
  return context
}

export default SessionContext
